/* ==========================================================================
   LV- Collection Rail — Laverne Diamonds
   assets/lv-rail.js

   Custom element so the Shopify editor lifecycle is native.

   - pointer drag that works with a mouse AND touch
   - a click after a drag is suppressed, so slides stay clickable
   - arrows disable at each end, progress bar follows the scroll
   - RTL-safe: direction is read at runtime, never hard-coded
   - keyboard: arrows move the rail, slides remain tabbable
   ========================================================================== */
(() => {
  'use strict';

  if (customElements.get('lv-rail')) return;

  const REDUCED = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const DRAG_THRESHOLD = 8; // px before a press counts as a drag, not a click

  class LVRail extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('[data-rail]');
      this.view = this.querySelector('[data-viewport]');
      if (!this.root || !this.view) return;

      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.bar = this.querySelector('[data-progress]');
      this.items = Array.from(this.view.children);

      this.rtl = getComputedStyle(this.root).direction === 'rtl';

      this.bindScroll();
      this.bindDrag();
      this.bindArrows();
      this.bindKeys();
      this.reveal();
      this.update();
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this._ro) this._ro.disconnect();
      clearTimeout(this._resizeTimer);
      this.view.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      this.unbindDrag();
    }

    /* ---- state ------------------------------------------------------------ */
    bindScroll() {
      let ticking = false;
      this._onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { ticking = false; this.update(); });
      };
      this.view.addEventListener('scroll', this._onScroll, { passive: true });

      /* Resize fires in a burst while a window is dragged. Re-measuring on
         every one of those is pointless; only the final size matters. */
      this._onResize = () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => {
          this.measure();
          this.update();
        }, 120);
      };
      window.addEventListener('resize', this._onResize, { passive: true });

      /* A lazy-loaded card image changes scrollWidth after the first measure,
         which would leave the arrows and progress bar wrong until a resize.
         ResizeObserver on the track catches that without polling. */
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => {
          this.measure();
          this.update();
        });
        this._ro.observe(this.view);
      }
    }

    update() {
      /* scrollWidth and clientWidth are layout reads. Calling them on every
         scroll frame forces the browser to flush pending style and layout
         work before it can answer — that is a forced synchronous layout, and
         it is the single most expensive thing you can do in a scroll handler.
         They only change on resize or when the content changes, so they are
         measured once and cached. */
      if (this._max == null) this.measure();
      const max = this._max;
      // scrollLeft is negative in RTL on most engines; normalise to 0..max.
      const raw = this.view.scrollLeft;
      const pos = this.rtl ? Math.abs(raw) : raw;
      const p = max > 1 ? Math.min(1, Math.max(0, pos / max)) : 1;

      if (this.bar) this.root.style.setProperty('--lv-progress', p.toFixed(4));

      const atStart = pos <= 1;
      const atEnd = pos >= max - 1;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;

      // Nothing to scroll: hide the controls rather than show dead buttons.
      // Cached too — querySelector on every frame is wasted work.
      if (this._nav === undefined) this._nav = this.querySelector('[data-nav]');
      if (this._nav) this._nav.hidden = max <= 1;
      if (this.bar) this.bar.hidden = max <= 1;
    }

    /* The only place layout is read. Called on init, on resize, and whenever
       images finish loading — never from the scroll handler. */
    measure() {
      this._max = this.view.scrollWidth - this.view.clientWidth;
      const first = this.items[0];
      this._step = first ? first.getBoundingClientRect().width : this.view.clientWidth * 0.8;
    }

    step() {
      if (this._step == null) this.measure();
      return this._step;
    }

    scrollBy(dir) {
      const delta = this.step() * dir * (this.rtl ? -1 : 1);
      this.view.scrollBy({ left: delta, behavior: REDUCED() ? 'auto' : 'smooth' });
    }

    bindArrows() {
      if (this.prev) this.prev.addEventListener('click', () => this.scrollBy(-1));
      if (this.next) this.next.addEventListener('click', () => this.scrollBy(1));
    }

    bindKeys() {
      // Bound to the section, not the viewport: the viewport no longer takes
      // focus (its tabindex was removed because focusing a snap container makes
      // the browser scroll it, which cancels clicks). Arrow keys work whenever
      // focus is anywhere inside the rail — including on the arrow buttons.
      this.root.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        // never hijack arrows while typing
        const t = e.target;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        e.preventDefault();
        // Visual direction, not logical: ArrowRight always moves right.
        const forward = e.key === 'ArrowRight';
        this.scrollBy(this.rtl ? (forward ? -1 : 1) : (forward ? 1 : -1));
      });
    }

    /* ---- drag --------------------------------------------------------------
       Pointer events cover mouse, touch and pen with one code path. Native
       touch scrolling is left alone; only mouse drags are hijacked, so the
       rail never fights the phone's own momentum.

       PERFORMANCE — why this is written the way it is

       1. pointermove fires far faster than the screen repaints, up to ~1000Hz
          on a high-rate mouse. Writing scrollLeft on every one of those events
          queues a layout per event and is what makes the drag feel like it is
          catching. The handler now only records the position; a single rAF
          does the write, so there is at most one per frame.

       2. The move and up listeners are attached on pointerdown and removed on
          pointerup, not left on window for the life of the page. A window
          pointermove listener runs for every mouse move anywhere on the
          document, for every rail on the page, even when nothing is being
          dragged.

       3. No setPointerCapture. Capturing retargets every subsequent event —
          including the final click — to the element holding the capture, so
          the browser never sees press-and-release on the same <a> and the
          card never opens. Attaching to window achieves the same "keep the
          gesture alive off-element" result without touching targeting.
       ---------------------------------------------------------------------- */
    bindDrag() {
      let startX = 0;
      let startScroll = 0;
      let targetScroll = 0;
      let dragging = false;
      let armed = false;
      let raf = 0;

      const flush = () => {
        raf = 0;
        if (!dragging) return;
        this.view.scrollLeft = targetScroll;
      };

      this._onMove = (e) => {
        if (!armed) return;
        const dx = e.clientX - startX;

        if (!dragging) {
          if (Math.abs(dx) <= DRAG_THRESHOLD) return;   // still might be a click
          dragging = true;
          this.view.classList.add('is-dragging');
        }
        // Stop text selection dragging along with the rail.
        if (e.cancelable) e.preventDefault();

        targetScroll = startScroll - dx;
        // Coalesce to one write per frame.
        if (!raf) raf = requestAnimationFrame(flush);
      };

      this._onUp = () => {
        if (!armed) return;
        armed = false;

        // Listeners only exist for the duration of a gesture.
        window.removeEventListener('pointermove', this._onMove);
        window.removeEventListener('pointerup', this._onUp);
        window.removeEventListener('pointercancel', this._onUp);

        if (raf) { cancelAnimationFrame(raf); raf = 0; }

        if (dragging) {
          // Apply the last position before the flag flips, or a fast release
          // can drop the final few pixels.
          this.view.scrollLeft = targetScroll;
          this.view.classList.remove('is-dragging');
          // Swallow the click this drag is about to produce. The flag is
          // cleared by the click itself, by the next pointerdown, and by a
          // timer — three independent releases, because a drag does not always
          // produce a click and a stuck flag would kill every later card.
          this._suppressClick = true;
          clearTimeout(this._suppressTimer);
          this._suppressTimer = setTimeout(() => { this._suppressClick = false; }, 400);
        }
        dragging = false;
        this.update();
      };

      this._onDown = (e) => {
        if (e.pointerType === 'touch') return;   // let the browser do it
        if (e.button !== 0) return;
        // Any new press cancels a stale suppression left by an earlier drag.
        this._suppressClick = false;
        armed = true;
        dragging = false;
        startX = e.clientX;
        startScroll = this.view.scrollLeft;
        targetScroll = startScroll;

        // Not passive: the move handler calls preventDefault to kill selection.
        window.addEventListener('pointermove', this._onMove, { passive: false });
        window.addEventListener('pointerup', this._onUp);
        window.addEventListener('pointercancel', this._onUp);
      };

      this._onClickCapture = (e) => {
        if (!this._suppressClick) return;
        this._suppressClick = false;
        clearTimeout(this._suppressTimer);
        e.preventDefault();
        e.stopPropagation();
      };

      this.view.addEventListener('click', this._onClickCapture, true);
      this.view.addEventListener('pointerdown', this._onDown);
      // A native drag on images would otherwise start a ghost image.
      this._onDragStart = (e) => e.preventDefault();
      this.view.addEventListener('dragstart', this._onDragStart);
    }

    unbindDrag() {
      if (!this._onDown) return;
      clearTimeout(this._suppressTimer);
      if (this._onClickCapture) this.view.removeEventListener('click', this._onClickCapture, true);
      this.view.removeEventListener('pointerdown', this._onDown);
      this.view.removeEventListener('dragstart', this._onDragStart);
      // These live on window, so they MUST be removed or the section leaks a
      // listener every time the theme editor re-renders it.
      window.removeEventListener('pointermove', this._onMove);
      window.removeEventListener('pointerup', this._onUp);
      window.removeEventListener('pointercancel', this._onUp);
    }

    /* ---- reveal ------------------------------------------------------------ */
    reveal() {
      const items = this.root.querySelectorAll('[data-animate]');
      if (!items.length) return;

      if (!('IntersectionObserver' in window) || REDUCED()) {
        this.root.classList.add('is-in');
        return;
      }
      this.root.classList.add('anim-ready');

      if (this.root.getBoundingClientRect().top < innerHeight * 0.9) {
        requestAnimationFrame(() => this.root.classList.add('is-in'));
        return;
      }
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          this.root.classList.add('is-in');
          this._io.disconnect();
        });
      }, { threshold: 0.12 });
      this._io.observe(this.root);
    }
  }

  customElements.define('lv-rail', LVRail);
})();
